import React, { useEffect, useCallback } from "react";
import debounce from "lodash.debounce";
import {
  Stack,
  Typography,
  Box,
  TextField,
  FormControlLabel,
  Checkbox,
  FormHelperText,
  Card,
  CardContent,
  Alert,
  Button,
  Tooltip,
} from "@mui/material";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import { useBillingStatus } from "src/hooks/use-billing";
import { paths } from "src/paths";
import { LocalizationProvider, TimePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  defaultIntervalDays,
  defaultMaxReminders,
  defaultPreferredTime,
} from "src/constants/approvalDefaults";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "src/app/store";
import { goToStep4, updateFormValues } from "src/features/approvalsCreate/approvalCreateSlice";

const NotificationSchema = Yup.object().shape({
  anc_preferred_time: Yup.string().required("Horário é obrigatório"),
  anc_max_reminders: Yup.number()
    .nullable()
    .required("Número máximo de envios é obrigatório")
    .min(1, "Número máximo de envios deve ser pelo menos 1"),
  anc_interval_days: Yup.number()
    .nullable()
    .required("Frequência de envio é obrigatória")
    .min(1, "Frequência de envio deve ser maior que 0"),
  anc_whatsapp_enabled: Yup.boolean(),
  anc_email_enabled: Yup.boolean(),
});

export const ApprovalNotification: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const formValues = useSelector((state: RootState) => state.approvalCreate.formValues);
  const loading = useSelector(
    (state: RootState) => state.approvalCreate.stages.step4.status === "loading",
  );
  const { data: billing } = useBillingStatus();
  const planSlug = billing?.plan?.slug;
  const normalizedPlanSlug = typeof planSlug === "string" ? planSlug.toLowerCase() : undefined;
  const isFreePlan = normalizedPlanSlug === "free";
  const canToggleWhatsApp = !isFreePlan;

  useEffect(() => {
    if (canToggleWhatsApp) {
      return;
    }

    if (formValues.anc_whatsapp_enabled) {
      dispatch(
        updateFormValues({
          ...formValues,
          anc_whatsapp_enabled: false,
        }),
      );
    }
  }, [canToggleWhatsApp, dispatch, formValues]);

  const debouncedUpdateFormValues = useCallback(
    debounce(
      (data: {
        anc_preferred_time: string;
        anc_interval_days: number;
        anc_max_reminders: number;
        anc_whatsapp_enabled: boolean;
        anc_email_enabled: boolean;
      }) => {
        dispatch(updateFormValues(data));
      },
      300, // Aumentado para 300ms para evitar inconsistências
    ),
    [dispatch],
  );

  const initialValues = {
    ...formValues,
    anc_whatsapp_enabled: canToggleWhatsApp ? formValues.anc_whatsapp_enabled : false,
  };

  const handleSubmit = async (values: typeof initialValues) => {
    dispatch(updateFormValues(values));
    const result = await dispatch(goToStep4());
    if (goToStep4.fulfilled.match(result)) {
      console.log("Success:", result.payload);
    } else {
      console.error("Error:", result.payload);
    }
  };

  const handleFormChange = (
    event: React.FormEvent<HTMLFormElement>,
    values: typeof initialValues,
  ) => {
    const formData = new FormData(event.currentTarget);
    const updatedValues = {
      anc_preferred_time:
        (formData.get("anc_preferred_time") as string) || values.anc_preferred_time,
      anc_interval_days: Number(formData.get("anc_interval_days")),
      anc_max_reminders: Number(formData.get("anc_max_reminders")),
      anc_whatsapp_enabled: canToggleWhatsApp
        ? Boolean(formData.get("anc_whatsapp_enabled"))
        : false,
      anc_email_enabled: Boolean(formData.get("anc_email_enabled")),
    };
    debouncedUpdateFormValues(updatedValues);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Card sx={{ m: 2 }}>
        <CardContent>
          <Stack
            spacing={1}
            direction="column"
            alignItems="flex-start"
            justifyContent="space-between"
            mb={2}
          >
            <Typography gutterBottom variant="h5">
              Configuração de Notificações
            </Typography>
            <Typography variant="caption">
              Defina como os aprovadores serão notificados e a frequência dos lembretes.
            </Typography>
          </Stack>

          <Formik
            initialValues={initialValues}
            validationSchema={NotificationSchema}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {({ values, setFieldValue, errors, touched }) => {
              return (
                <Form onChange={(event) => handleFormChange(event, values)}>
                  <Stack spacing={2}>
                    <Box sx={{ backgroundColor: "action.hover", borderRadius: 2, p: 2, mb: 2 }}>
                      <Typography variant="subtitle1">Canais de notificações</Typography>
                      <Typography variant="body2" color="textSecondary" mb={1}>
                        Defina como os aprovadores serão notificados
                      </Typography>

                      <FormControlLabel
                        disabled={loading || !canToggleWhatsApp}
                        control={
                          <Field
                            as={Checkbox}
                            type="checkbox"
                            name="anc_whatsapp_enabled"
                            checked={values.anc_whatsapp_enabled}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                              if (!canToggleWhatsApp) {
                                return;
                              }

                              setFieldValue("anc_whatsapp_enabled", event.target.checked);
                              debouncedUpdateFormValues({
                                ...values,
                                anc_whatsapp_enabled: event.target.checked,
                              });
                            }}
                          />
                        }
                        label={
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <Typography>WhatsApp</Typography>
                            {!canToggleWhatsApp && (
                              <Tooltip title="Disponível apenas em planos pagos">
                                <WorkspacePremiumIcon
                                  fontSize="small"
                                  sx={(theme) => ({
                                    color:
                                      theme.palette.mode === "light"
                                        ? theme.palette.warning.dark
                                        : theme.palette.warning.light,
                                  })}
                                />
                              </Tooltip>
                            )}
                          </Box>
                        }
                      />
                      <Typography variant="body2" color="textSecondary">
                        Todos os aprovadores receberão uma mensagem no WhatsApp
                      </Typography>
                      {isFreePlan && (
                        <Alert
                          severity="warning"
                          title="teste"
                          sx={{ mt: 1, borderRadius: 1 }}
                          action={
                            <Button
                              size="small"
                              color="warning"
                              variant="contained"
                              href={paths.dashboard.organization.changePlan}
                            >
                              Upgrade
                            </Button>
                          }
                        >
                          O envio de notificações por WhatsApp não está disponível no plano
                          gratuito. Faça upgrade para habilitar este recurso.
                        </Alert>
                      )}
                      <FormControlLabel
                        disabled={loading}
                        control={
                          <Field
                            as={Checkbox}
                            type="checkbox"
                            name="anc_email_enabled"
                            checked={values.anc_email_enabled}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                              setFieldValue("anc_email_enabled", event.target.checked);
                              debouncedUpdateFormValues({
                                ...values,
                                anc_email_enabled: event.target.checked,
                              });
                            }}
                          />
                        }
                        label="E-mail"
                      />
                      <Typography variant="body2" color="textSecondary">
                        Todos os aprovadores receberão um e-mail
                      </Typography>
                    </Box>

                    <Box sx={{ backgroundColor: "action.hover", borderRadius: 2, p: 2 }}>
                      <Typography variant="subtitle1" gutterBottom>
                        Horário para envio
                      </Typography>
                      <Typography variant="body2" color="textSecondary" mb={2}>
                        Qual horário você gostaria de enviar os lembretes?
                      </Typography>
                      <TimePicker
                        label="Horário para envio"
                        disabled={loading}
                        openTo="hours"
                        views={["hours", "minutes"]}
                        minTime={dayjs("00:00")}
                        maxTime={dayjs("23:55")}
                        ampm={false}
                        value={
                          values.anc_preferred_time
                            ? dayjs(values.anc_preferred_time, "HH:mm")
                            : null
                        }
                        onChange={(newValue) => {
                          const formattedTime = newValue?.format("HH:mm") || "";
                          setFieldValue("anc_preferred_time", formattedTime);
                          debouncedUpdateFormValues({
                            ...values,
                            anc_preferred_time: formattedTime,
                          });
                        }}
                        slotProps={{ textField: { fullWidth: true } }}
                      />
                      {errors.anc_preferred_time && touched.anc_preferred_time && (
                        <FormHelperText error>{errors.anc_preferred_time}</FormHelperText>
                      )}
                    </Box>

                    <Box sx={{ backgroundColor: "action.hover", borderRadius: 2, p: 2 }}>
                      <Typography variant="subtitle1" gutterBottom>
                        Recorrência das Notificações (em dias)
                      </Typography>
                      <Typography variant="body2" color="textSecondary" mb={2}>
                        Informe após quantos dias o aprovador deve receber um novo lembrete.
                      </Typography>
                      <Field
                        as={TextField}
                        name="anc_interval_days"
                        type="number"
                        label="Recorrência das Notificações (em dias)"
                        placeholder="Ex: 3"
                        fullWidth
                        disabled={loading}
                      />
                      {errors.anc_interval_days && touched.anc_interval_days && (
                        <FormHelperText error>{errors.anc_interval_days}</FormHelperText>
                      )}
                    </Box>

                    <Box sx={{ backgroundColor: "action.hover", borderRadius: 2, p: 2 }}>
                      <Typography variant="subtitle1">Número máximo de envios</Typography>
                      <Typography variant="body2" color="textSecondary" mb={2}>
                        Quantidade máxima de lembretes que o aprovador receberá, após isso o
                        aprovador não receberá mais lembretes.
                      </Typography>
                      <Field
                        as={TextField}
                        name="anc_max_reminders"
                        type="number"
                        label="Número máximo de envios"
                        placeholder="Ex: 3"
                        fullWidth
                        disabled={loading}
                      />
                      {errors.anc_max_reminders && touched.anc_max_reminders && (
                        <FormHelperText error>{errors.anc_max_reminders}</FormHelperText>
                      )}
                    </Box>
                  </Stack>
                </Form>
              );
            }}
          </Formik>
        </CardContent>
        <Alert severity="info" sx={{ borderRadius: 0 }}>
          Os lembretes serão enviados apenas para os aprovadores que não tiverem aprovado a
          solicitação.
        </Alert>
      </Card>
    </LocalizationProvider>
  );
};
